import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import autoMappingSummary from "@ChannelCreator/components/AutoMapping/AutoMappingSummary.vue";
import { useToast } from "@common/composable/toast";
import { MappingGenerationResultModel, SignalRResponse } from "@medapro-types/ChannelCreator/components/AutoMappingTab";
import { CatalogGeneratorResult } from "@medapro-types/ChannelCreator/components/CatalogGeneration";
import { ChannelCreatorState } from "@medapro-types/ChannelCreator/store/channel-creator-store";
import { ColorVariants } from "@medapro-types/common/common";
import { RootActions } from "@medapro-types/common/rootstate";
import { TranslationsResponseType } from "@medapro-types/ContentEditor/Components/Article/TranslationArticleEditor";
import { ProgressEntry } from "@medapro-types/common/components/Progress";
import { ProgressStatus, ProgressType } from "@medapro-types/common/common-enum";
import { h, inject, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { createNamespacedHelpers, useActions } from "vuex-composition-helpers";
import { useDownload } from "@common/util/common";
import { useRouter } from "vue-router";

//#region Inject
const maxSupportedToastWordLength = 18;
//#endregion

//#region store
type ViewModel = {
  mappingSummary: MappingGenerationResultModel;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SignalRNotifier',
  setup(__props) {

const $signalRStartedPromise = inject<Promise<void>>("$signalRStartedPromise");
const $subscribeToServerEvent = inject<(event: string, handler: any) => void>("$subscribeToServerEvent");
const $unsubscribeFromServerEvent = inject<(event: string, handler: any) => void>("$unsubscribeFromServerEvent");
//#endregion

//#region General
const { t, te } = useI18n();
const { createToast, closeToast } = useToast();
const autoMappingSummaryModal = ref<typeof autoMappingSummary>();
const $refs = { autoMappingSummaryModal };
const { downloadExportByRunID } = useDownload();
const router = useRouter();
const { saveUserSettings } = useActions<RootActions>(["saveUserSettings"]);

const {
  useActions: useChannelCreatorActions,
  useMutations: useChannelCreatorMutations,
  useGetters: useChannelCreatorGetters
} = createNamespacedHelpers<ChannelCreatorState>("channelCreator");
const { loadCatalogs, updateTargetCatalog } = useChannelCreatorActions(["loadCatalogs", "updateTargetCatalog"]);
const { clearDirtyStatus: clearChannelCreatorDirtyStatus } = useChannelCreatorMutations(["clearDirtyStatus"]);
const { hasUnsavedData } = useChannelCreatorGetters(["hasUnsavedData"]);

const { useMutations: useCatalogNodeMutation } = createNamespacedHelpers("contentEditor/catalogNode");
const { "clear-dirty-status": clearCatalogNodeDirtyStatus } = useCatalogNodeMutation(["clear-dirty-status"]);
//#endregion

const vm = reactive<ViewModel>({
  mappingSummary: {
    catalogMappingsCount: 0,
    invariantAttributeMappingsCount: 0,
    variantAttributeMappingsCount: 0,
    globalMappingsCount: 0,
    projectName: "",
    catalogName: "",
    targetSystem: ""
  }
});

function setProgressStatus(progress: ProgressEntry) {
  if (progress.progress.isFinished && progress.progress.percentage == 100) {
    const module = progress.responseType.toLowerCase() as ProgressType;
    let text;
    let title;
    let toastVariant: ColorVariants;
    if (progress.progress.title == ProgressStatus.Completed) {
      title = t(`${module}.progress.completed`);
      text = t(`${module}.progress.completedText`, { name: progress.name });
      toastVariant = "success";
    } else if (progress.progress.title == ProgressStatus.Faulted) {
      title = t(`${module}.progress.faulted`);
      text = t(`${module}.progress.failedText`, { name: progress.name });
      toastVariant = "danger";
    } else if (progress.progress.title == ProgressStatus.Cancelled) {
      title = t(`${module}.progress.cancelled`);
      text = t(`${module}.progress.cancelledText`, { name: progress.name });
      toastVariant = "danger";
    } else if (progress.progress.title == ProgressStatus.CompletedWithError) {
      title = t(`${module}.progress.completedwitherror`);
      text = t(`${module}.progress.completedwitherrorText`, { name: progress.name });
      toastVariant = "success";
    } else return;

    createToast({
      text: text,
      title: title,
      variant: toastVariant,
      autoClose: true,
      primaryActionButton: module == ProgressType.Export && progress.progress.title == ProgressStatus.Completed ? t("download") : undefined,
      secondaryActionButton: t("progress.openLog"),
      primaryAction: () => {
        downloadExportByRunID(progress.runId);
      },
      secondaryAction: () => {
        const targetType = progress.targetType ? progress.targetType : "";
        let routeName = "";
        if (module == ProgressType.Export) {
          routeName = "progress-export";
        } else if (module == ProgressType.Import) {
          routeName = "progress-import";
        }
        const routeUrl = router.resolve({
          name: routeName,
          params: { type: targetType, id: progress.runId }
        }).href;
        if (routeUrl) window.open(routeUrl);
      }
    });
  }
}

function onMediaConversionCompleted(articleNo: string) {
  createToast({
    text: t("mediaConversionMessage", {
      article: articleNo
    }),
    title: t("mediaConversionCompleted"),
    variant: "success",
    textIllustration: "MediaAsset",
    statusTextIcon: "CheckCircleSolid"
  });
}

function onAutoMappingCompleted(response: SignalRResponse) {
  if (response.isSuccess) {
    createToast({
      text: [
        t("Automapping.AutomappingCompletedMessage", {
          mappingprojectname: response?.result?.projectName,
          catalogname: response?.result?.catalogName
        })
      ],
      title: t("Automapping.AutomappingCompleted"),
      variant: "success",
      autoClose: true,
      primaryActionButton: t("summary"),
      primaryAction: () => {
        vm.mappingSummary = response.result;
        $refs.autoMappingSummaryModal.value?.show();
      }
    });
  } else {
    console.error(
      `Server notification recieved for the auto mapping failure of mapping project "${response?.result?.catalogName}" was carried out on catalogue "${response?.result?.catalogName}" with error - "${response.errorMessage}" `
    );
    const errorMessage = te(`api.errors.${response.errorMessage}`) ? t(`api.errors.${response.errorMessage}`) : "";

    createToast({
      text:
        t("Automapping.AutomappingFailedWithError", {
          mappingprojectname: response?.result?.catalogName,
          catalogname: response?.result?.catalogName
        }) +
        " " +
        errorMessage,
      title: t("Automapping.AutomappingFailed"),
      variant: "warning",
      autoClose: true
    });
  }
}

function onTranslationFromMemoryCompleted(response: TranslationsResponseType) {
  const variationGroupNameFormatted =
    response.variationGroupName.length > maxSupportedToastWordLength ? response.variationGroupName.slice(0, 18) + "..." : response.variationGroupName;
  createToast({
    text: response.isSuccess
      ? t("translation.translationAllValuesMessage", {
          variationGroup: variationGroupNameFormatted
        })
      : t("translation.translationAllValuesErrorMessage", { variationGroup: variationGroupNameFormatted }),
    title: response.isSuccess ? t("translation.alertTitleSuccess") : t("translation.alertTitleOnError"),
    variant: response.isSuccess ? "success" : "danger",
    titleIcon: "Content-Editor",
    textIllustration: "DeepL-black"
  });
}

function onTranslationByDeepLCompleted(response: TranslationsResponseType) {
  const variationGroupNameFormatted =
    response.variationGroupName.length > maxSupportedToastWordLength ? response.variationGroupName.slice(0, 18) + "..." : response.variationGroupName;
  createToast({
    text: response.isSuccess
      ? t("translation.translationDeepLValuesMessage", {
          variationGroup: variationGroupNameFormatted
        })
      : t("translation.translationDeepLValuesErrorMessage", { variationGroup: variationGroupNameFormatted }),
    title: response.isSuccess ? t("translation.alertTitleSuccess") : t("translation.alertTitleOnError"),
    variant: response.isSuccess ? "success" : "danger",
    titleIcon: "Content-Editor",
    textIllustration: "DeepL-black"
  });
}

function onCatalogGenerationFromFilterCompleted(response: CatalogGeneratorResult) {
  if (response.isValid) {
    createToast({
      text: [
        h("div", { class: "font-weight-bold" }, t("newCatalogAvailable")),
        h("div", { class: "font-weight-bold" }, response.catalogName),
        t("articleInVariationGroup", {
          articlesInCatalog: response?.articlesInCatalog,
          variationGroups: response?.variationGroups
        })
      ],
      title: t("catalogGenerated"),
      id: "toast_from_generate_catalog",
      variant: "success",
      autoClose: false,
      textIllustration: "CatalogNode",
      statusTextIcon: "CheckCircleSolid",
      primaryActionButton: t("OpenCatalog"),
      primaryAction: async () => {
        if (isChannelCreatorRouteAndIsDirty()) {
          if (!confirm(t("confirmNavigation"))) {
            return;
          }
        }
        loadCatalogs().then(() => {
          //Timeout prevents JStree bug from loading it twice.
          setTimeout(() => {
            updateTargetCatalog(response.catalogId);
            saveUserSettings({ ChannelCreatorTargetCatalogId: response.catalogId });
            clearChannelCreatorDirtyStatus();
            clearCatalogNodeDirtyStatus();
          }, 100);
          closeToast("toast_from_generate_catalog");
        });
      },
      secondaryActionButton: t("dismiss"),
      secondaryAction: () => {
        loadCatalogs();
        closeToast("toast_from_generate_catalog");
      }
    });
  } else {
    console.error(`Server notification recieved for the catalog generation failure  with error - "${response.error}" `);
    let errorMessage = "";
    if (response.error == "NoArticlesFromSourceInCatalog")
      errorMessage =
        t("ErrorMessage.NoArticlesInSourceCatalogue") + "\n'" + (response.sourceCatalogName ?? "") + "'. " + t("ErrorMessage.NoCatalogueGenerated");
    else if (te("ErrorMessage." + response.error)) errorMessage = t("ErrorMessage." + response.error);
    else errorMessage = response.error;

    createToast({
      text: errorMessage,
      title: t("ErrorMessage.CatalogueGenerationFailed"),
      variant: "warning",
      autoClose: false
    });
  }
}

function isChannelCreatorRouteAndIsDirty() {
  const currentRoute = router.currentRoute.value;
  const isChannelCreatorRoute = (currentRoute.meta as any)?.module?.startsWith("channel-creator");
  const isDirty = hasUnsavedData.value;

  if (isChannelCreatorRoute && isDirty) {
    return true;
  }
  return false;
}

async function startEventSubscription() {
  await $signalRStartedPromise;
  if ($subscribeToServerEvent) {
    $subscribeToServerEvent("ExportImportProgressStatus", setProgressStatus);
    $subscribeToServerEvent("MediaConversionCompleted", onMediaConversionCompleted);
    $subscribeToServerEvent("AutoMappingCompleted", onAutoMappingCompleted);
    $subscribeToServerEvent("TranslationFromMemoryCompleted", onTranslationFromMemoryCompleted);
    $subscribeToServerEvent("TranslationByDeepLCompleted", onTranslationByDeepLCompleted);
    $subscribeToServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

async function unSubscribeEvent() {
  if ($unsubscribeFromServerEvent) {
    $unsubscribeFromServerEvent("ExportImportProgressStatus", setProgressStatus);
    $unsubscribeFromServerEvent("MediaConversionCompleted", onMediaConversionCompleted);
    $unsubscribeFromServerEvent("AutoMappingCompleted", onAutoMappingCompleted);
    $unsubscribeFromServerEvent("TranslationFromMemoryCompleted", onTranslationFromMemoryCompleted);
    $unsubscribeFromServerEvent("TranslationByDeepLCompleted", onTranslationByDeepLCompleted);
    $unsubscribeFromServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

//#region LifeCycle
onMounted(() => {
  startEventSubscription();
});

onBeforeUnmount(() => {
  unSubscribeEvent();
});
//#endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(autoMappingSummary, {
    ref_key: "autoMappingSummaryModal",
    ref: autoMappingSummaryModal,
    summary: vm.mappingSummary
  }, null, 8, ["summary"]))
}
}

})